import styled, { css, keyframes } from 'styled-components';

/* ===========================================================
   Arcana Vision — Primitivas técnicas/HUD reutilizables
   =========================================================== */

export const accent = 'var(--btn-primary)';
export const accentSoft = 'rgba(0, 180, 216, 0.14)';
export const success = 'var(--alert-success)';
export const warning = 'var(--alert-warning)';
export const danger = 'var(--alert-error)';

export const borderSoft = 'rgba(255, 255, 255, 0.06)';
export const borderStrong = 'rgba(255, 255, 255, 0.14)';
export const panel = 'var(--color-tertiary)';
export const panelDeep = 'rgba(11, 19, 32, 0.55)';
export const mono = "'JetBrains Mono', 'Fira Code', 'Consolas', monospace";

/* esquinas técnicas como mixin */
export const techCorners = css`
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
    opacity: 0.9;
  }
  &::before {
    top: -1px; left: -1px;
    border-top-width: 1px; border-left-width: 1px;
  }
  &::after {
    bottom: -1px; right: -1px;
    border-bottom-width: 1px; border-right-width: 1px;
  }
`;

export const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(6, 214, 160, 0.55); }
  50%     { box-shadow: 0 0 0 5px rgba(6, 214, 160, 0); }
`;

export const spin = keyframes`to { transform: rotate(360deg); }`;

/* ---------- contenedor de página con grid bg ---------- */

export const TechShell = styled.div`
  position: relative;
  padding: 0.5rem 0 2rem;

  &::before {
    content: '';
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
    background-size: 32px 32px;
    mask-image: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.6), transparent 75%);
  }

  > * { position: relative; z-index: 1; }
`;

/* ---------- header de página ---------- */

export const TechHeader = styled.header`
  position: relative;
  margin-bottom: 1.5rem;
  padding: 1.1rem 1.4rem;
  background:
    linear-gradient(135deg, rgba(0, 180, 216, 0.06), transparent 55%),
    rgba(36, 52, 77, 0.7);
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  backdrop-filter: blur(6px);
  ${techCorners}
`;

export const TechTitle = styled.h1`
  margin: 0 0 0.4rem;
  color: var(--text-primary);
  font-size: ${({ size }) => size || '1.45rem'};
  letter-spacing: -0.005em;
  display: flex;
  align-items: center;
  gap: 0.6rem;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${accent};
    box-shadow: 0 0 0 3px ${accentSoft};
    flex-shrink: 0;
  }
`;

export const TechSubtitle = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.92rem;
`;

export const DossierTag = styled.span`
  margin-left: auto;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${accent};
  padding: 3px 8px;
  border: 1px solid ${borderStrong};
  border-radius: 4px;
  background: rgba(11, 19, 32, 0.4);
`;

/* tira de pills de estado debajo del header */

export const StatusStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

export const StatusPill = styled.span`
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-disabled);
  border: 1px solid ${borderSoft};
  background: rgba(11, 19, 32, 0.5);
  padding: 3px 8px;
  border-radius: 3px;

  strong {
    color: ${accent};
    margin-left: 0.4rem;
    font-weight: 600;
  }
`;

/* ---------- panel base ---------- */

export const TechPanel = styled.section`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    ${panel};
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: ${({ pad }) => pad || '1.1rem 1.15rem'};
  ${techCorners}
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0 0 1rem;
  padding: 0 0 0.6rem;
  border-bottom: 1px solid ${borderSoft};
`;

export const PanelTitle = styled.h3`
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.55rem;

  svg { color: ${accent}; }
`;

export const PanelDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${accent};
  box-shadow: 0 0 0 3px ${accentSoft};
`;

/* ---------- stat tile / strip ---------- */

export const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${({ min }) => min || '150px'}, 1fr));
  gap: 0.55rem;
`;

export const StatTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${borderSoft};
  border-radius: 4px;
  background: ${panelDeep};
`;

export const StatLabel = styled.span`
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  display: flex;
  align-items: center;
  gap: 0.35rem;

  svg { font-size: 0.7rem; color: ${accent}; }
`;

export const StatValue = styled.span`
  font-family: ${mono};
  font-size: ${({ size }) => size || '1.25rem'};
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
`;

export const StatDelta = styled.span`
  font-family: ${mono};
  font-size: 0.7rem;
  color: ${({ positive }) => (positive ? 'var(--alert-success)' : 'var(--alert-error)')};
`;

export const StatSub = styled.span`
  font-family: ${mono};
  font-size: 0.7rem;
  color: var(--text-disabled);
`;

/* ---------- chips / badges ---------- */

export const TechChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.22rem 0.6rem;
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  color: ${({ tone }) =>
    tone === 'success' ? success
      : tone === 'warn' ? warning
      : tone === 'danger' ? danger
      : tone === 'mute' ? 'var(--text-disabled)'
      : accent};
  border: 1px solid ${({ tone }) =>
    tone === 'success' ? 'rgba(6, 214, 160, 0.4)'
      : tone === 'warn' ? 'rgba(255, 209, 102, 0.4)'
      : tone === 'danger' ? 'rgba(239, 71, 111, 0.4)'
      : tone === 'mute' ? borderSoft
      : 'rgba(0, 180, 216, 0.3)'};
  background: ${({ tone }) =>
    tone === 'success' ? 'rgba(6, 214, 160, 0.08)'
      : tone === 'warn' ? 'rgba(255, 209, 102, 0.08)'
      : tone === 'danger' ? 'rgba(239, 71, 111, 0.08)'
      : tone === 'mute' ? 'rgba(11, 19, 32, 0.5)'
      : 'rgba(0, 180, 216, 0.08)'};
  border-radius: 3px;
`;

export const Hashtag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-family: ${mono};
  font-size: 0.72rem;
  color: #cfe9f4;
  background: rgba(0, 180, 216, 0.08);
  border: 1px solid rgba(0, 180, 216, 0.25);
  border-radius: 3px;
  padding: 0.2rem 0.55rem;

  &::before {
    content: '#';
    color: ${accent};
    opacity: 0.85;
  }
`;

/* ---------- inputs / botones ---------- */

export const TechInput = styled.input`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 0.55rem 0.8rem;
  color: var(--text-primary);
  font-size: 0.92rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  width: 100%;

  &::placeholder { color: var(--text-disabled); }

  &:focus {
    border-color: rgba(0, 180, 216, 0.45);
    box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.1);
  }
`;

export const TechSelect = styled.select`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 0.5rem 0.7rem;
  color: var(--text-primary);
  font-size: 0.88rem;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  transition: border-color 0.18s ease;

  &:hover, &:focus { border-color: rgba(0, 180, 216, 0.4); }

  option {
    background: var(--color-tertiary);
    color: var(--text-primary);
  }
`;

export const TechTextarea = styled.textarea`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 0.7rem 0.85rem;
  color: var(--text-primary);
  font-size: 0.92rem;
  font-family: inherit;
  outline: none;
  resize: vertical;
  min-height: 90px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &::placeholder { color: var(--text-disabled); }
  &:focus {
    border-color: rgba(0, 180, 216, 0.45);
    box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.1);
  }
`;

const baseBtn = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.55rem 0.95rem;
  border-radius: 7px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;

  &:disabled { opacity: 0.45; cursor: not-allowed; }
  svg { font-size: 0.85rem; }
`;

export const TechButton = styled.button`
  ${baseBtn}
  color: ${accent};
  border: 1px solid rgba(0, 180, 216, 0.35);
  background: rgba(0, 180, 216, 0.08);

  &:hover:not(:disabled) {
    background: rgba(0, 180, 216, 0.16);
    border-color: rgba(0, 180, 216, 0.55);
    color: var(--text-primary);
  }
`;

export const TechButtonGhost = styled.button`
  ${baseBtn}
  color: var(--text-secondary);
  border: 1px solid ${borderSoft};
  background: transparent;

  &:hover:not(:disabled) {
    color: var(--text-primary);
    border-color: ${borderStrong};
    background: rgba(255, 255, 255, 0.03);
  }
`;

export const TechButtonDanger = styled.button`
  ${baseBtn}
  color: var(--alert-error);
  border: 1px solid rgba(239, 71, 111, 0.35);
  background: rgba(239, 71, 111, 0.06);

  &:hover:not(:disabled) {
    background: rgba(239, 71, 111, 0.14);
    border-color: rgba(239, 71, 111, 0.55);
    color: #ffd0db;
  }
`;

/* ---------- listas numeradas estilo HUD ---------- */

export const NumberedList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  counter-reset: tech;
`;

export const NumberedItem = styled.li`
  display: flex;
  gap: 0.7rem;
  padding: 0.6rem 0.8rem;
  border: 1px solid ${borderSoft};
  background: ${panelDeep};
  border-radius: 4px;
  font-size: 0.88rem;
  color: var(--text-secondary);
  line-height: 1.45;
  counter-increment: tech;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;

  &::before {
    content: counter(tech, decimal-leading-zero);
    font-family: ${mono};
    font-size: 0.7rem;
    color: ${accent};
    letter-spacing: 0.08em;
    flex-shrink: 0;
    min-width: 22px;
  }

  &:hover {
    border-color: rgba(0, 180, 216, 0.25);
    background: rgba(0, 180, 216, 0.05);
    transform: translateX(2px);
  }
`;

/* ---------- progress / barras ---------- */

export const TechBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 2px;
  overflow: hidden;
`;

export const TechBarFill = styled.div`
  height: 100%;
  width: ${({ value }) => Math.max(0, Math.min(100, value || 0))}%;
  background: ${({ color }) => color || accent};
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
  transition: width 0.4s ease;
`;

/* ---------- timeline ---------- */

export const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-left: 0.25rem;
`;

export const TimelineItem = styled.div`
  position: relative;
  padding-left: 1.4rem;
  border-left: 1px solid ${borderStrong};
  padding-bottom: 0.25rem;

  &::before {
    content: '';
    position: absolute;
    left: -5px;
    top: 4px;
    width: 9px;
    height: 9px;
    background: ${accent};
    border-radius: 50%;
    box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.15);
  }
`;

export const TimelineDate = styled.div`
  font-family: ${mono};
  font-size: 0.68rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${accent};
  margin-bottom: 0.15rem;
`;

/* ---------- estados ---------- */

export const Empty = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: var(--text-disabled);
  font-size: 0.92rem;
  border: 1px dashed ${borderSoft};
  border-radius: 6px;
`;

export const ErrorBox = styled(Empty)`
  color: var(--alert-error);
  border-color: rgba(239, 71, 111, 0.3);
`;

export const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 1.5rem;
  color: var(--text-disabled);
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border: 2px solid rgba(0, 180, 216, 0.25);
    border-top-color: ${accent};
    border-radius: 50%;
    animation: ${spin} 0.9s linear infinite;
  }
`;

/* ---------- grids comunes ---------- */

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ cols }) => cols || 12}, 1fr);
  gap: ${({ gap }) => gap || '1.25rem'};

  @media (max-width: 1200px) {
    grid-template-columns: repeat(${({ colsMd }) => colsMd || 6}, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const Span = styled.div`
  grid-column: span ${({ span }) => span || 12};
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

/* ---------- tabs estilo HUD ---------- */

export const TabsContainer = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border-bottom: 1px solid ${borderSoft};
  margin-bottom: 1.25rem;
  overflow-x: auto;

  &::-webkit-scrollbar { height: 4px; }
`;

export const Tab = styled.button`
  position: relative;
  padding: 0.7rem 1rem 0.85rem;
  background: none;
  border: none;
  color: ${({ active }) => (active ? 'var(--text-primary)' : 'var(--text-disabled)')};
  font-size: 0.82rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: inherit;
  transition: color 0.18s ease;

  .tab-index {
    font-family: ${mono};
    font-size: 0.65rem;
    letter-spacing: 0.18em;
    color: ${({ active }) => (active ? accent : 'var(--text-disabled)')};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${accent};
    transform: scaleX(${({ active }) => (active ? 1 : 0)});
    transform-origin: left;
    transition: transform 0.18s ease;
  }

  &:hover { color: var(--text-primary); }
`;

/* helper componente de tabs autoindexado */
export const TechTabs = ({ tabs, active, onChange }) => (
  <TabsContainer>
    {tabs.map((t, i) => (
      <Tab key={t.id} active={active === t.id} onClick={() => onChange(t.id)} type="button">
        <span className="tab-index">{String(i + 1).padStart(2, '0')}</span>
        {t.label}
      </Tab>
    ))}
  </TabsContainer>
);

/* helper componente de página */
export const TechPageHeader = ({
  title,
  subtitle,
  dossier,
  status,
  actions,
  children,
}) => (
  <TechHeader>
    <TechTitle>
      {title}
      {dossier && <DossierTag>{dossier}</DossierTag>}
      {actions && (
        <div style={{ marginLeft: dossier ? 0 : 'auto', display: 'flex', gap: '0.5rem' }}>
          {actions}
        </div>
      )}
    </TechTitle>
    {subtitle && <TechSubtitle>{subtitle}</TechSubtitle>}
    {(status || children) && (
      <StatusStrip>
        {status?.map((s, i) => (
          <StatusPill key={i}>
            {s.label} {s.value != null && <strong>{s.value}</strong>}
          </StatusPill>
        ))}
        {children}
      </StatusStrip>
    )}
  </TechHeader>
);

/* helper sección con título y dot */
export const TechSection = ({ title, icon, action, children, ...rest }) => (
  <TechPanel {...rest}>
    {(title || action) && (
      <PanelHeader>
        {title && (
          <PanelTitle>
            {!icon && <PanelDot />}
            {icon}
            {title}
          </PanelTitle>
        )}
        {action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
      </PanelHeader>
    )}
    {children}
  </TechPanel>
);
